import { useEffect, useState } from "react";
import axios from "axios";
import { Container, Typography, CircularProgress, Stack } from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import { VOICE_WEBRTC_CREDENTIALS_ENDPOINT } from "../../config/urls";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import Softphone from "./Softphone";

// Phase C (WhatsApp Voice agent desktop). This page fetches the tenant's
// WebRTC registration details and, once the SIP box has a WSS transport
// configured (see WHATSAPP_VOICE_HANDOFF.md §Phase C), hands them to the
// Softphone component. Until then websocket_url is blank and this page
// says so rather than trying to connect.
const Voice = () => {
  const { getValidToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchCredentials = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const response = await axios.get(VOICE_WEBRTC_CREDENTIALS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        });
        if (!cancelled) setCredentials(response.data);
      } catch (err) {
        console.error("Error fetching WebRTC credentials:", err);
        if (!cancelled) setError("Could not load voice calling settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCredentials();
    return () => {
      cancelled = true;
    };
  }, [getValidToken]);

  if (loading) {
    return (
      <Container sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography sx={typography.bodyLarge} color={ColorsConstants.error}>
          {error}
        </Typography>
      </Container>
    );
  }

  if (!credentials?.voice_enabled) {
    return (
      <Container sx={{ py: 6 }}>
        <Stack spacing={1}>
          <Typography sx={typography.bodyLarge}>
            Voice calling isn't enabled for your account yet.
          </Typography>
          <Typography sx={typography.bodyLarge} color={ColorsConstants.primaryGray60}>
            Reach out to your BotBridge contact to get set up.
          </Typography>
        </Stack>
      </Container>
    );
  }

  if (!credentials.websocket_url) {
    return (
      <Container sx={{ py: 6 }}>
        <Stack spacing={1}>
          <Typography sx={typography.bodyLarge}>
            Voice calling is enabled for your account, but the calling
            infrastructure isn't live yet.
          </Typography>
          <Typography sx={typography.bodyLarge} color={ColorsConstants.primaryGray60}>
            Check back soon.
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Softphone credentials={credentials} />
    </Container>
  );
};

export default Voice;
